import { useMemo } from 'react';
import { 
  APP_REGISTRY, 
  getApps, 
  getLiveApps, 
  getAppsByCategory, 
  getDashboardApps,
  generateRoutes 
} from './registry';
import type { AppDefinition, DashboardApp, RouteDefinition, AppCategory } from './types';

/**
 * Hook to get all apps from registry
 */
export const useAppRegistry = () => {
  return useMemo(() => getApps(), []);
};

/**
 * Hook to get live apps only
 */
export const useLiveApps = () => {
  return useMemo(() => getLiveApps(), []);
};

/**
 * Hook to get apps by category
 */
export const useAppsByCategory = (category: AppCategory): AppDefinition[] => {
  return useMemo(() => getAppsByCategory(category), [category]);
};

/**
 * Hook to get dashboard-formatted apps
 */
export const useDashboardApps = (): DashboardApp[] => {
  return useMemo(() => getDashboardApps(), []);
};

/**
 * Hook to get generated routes
 */
export const useRoutes = (): RouteDefinition[] => {
  return useMemo(() => generateRoutes(), []);
};

/**
 * Hook to filter accessible apps based on user permissions
 */
export const useAccessibleApps = (
  userApps: string[] = [],
  isAdmin: boolean = false
): DashboardApp[] => {
  const allApps = useDashboardApps();
  
  return useMemo(() => {
    if (isAdmin) {
      return allApps;
    }
    
    return allApps.filter(app => {
      // Check if user has permission for this app
      const appDef = APP_REGISTRY.find(a => a.id === app.id);
      const permissionId = appDef?.permissionId || app.id;
      return userApps.includes(permissionId);
    });
  }, [allApps, userApps, isAdmin]);
};

/**
 * Hook to check if user can access specific app
 */
export const useCanAccessApp = (
  appId: string,
  userApps: string[] = [],
  isAdmin: boolean = false
): boolean => {
  return useMemo(() => {
    if (isAdmin) return true;
    
    const app = APP_REGISTRY.find(a => a.id === appId);
    if (!app) return false;
    
    const permissionId = app.permissionId || appId;
    return userApps.includes(permissionId);
  }, [appId, userApps, isAdmin]);
};
